import React, { useEffect, useState, useRef } from "react";
import {
  Col,
  Modal,
  Row,
  Spin,
  Typography,
  Input,
  message,
  Popconfirm,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import EditIcon from "../../ui/EditIcon";
import NoDataPage from "../../NoDataPages/NoDataPage";
import GalleryIcon from "../../../assets/images/nurseIcons/gallery.svg";
import ReactPlayer from "react-player";
import GridPdfViewer from "../InvestigationsIp/InvestigationGallery/GridPdfViewer";
import PatientGalleryScroll from "./PatientGalleryScroll";
import "./PatientImageView.css";
import dayjs from "dayjs";
import {
  deletePatientUploadsApi,
  updateGalleryNameApi,
} from "../../../services/patient.services";
import {
  sortGalleryFilesByName,
  validateGalleryFileName,
  getGalleryFileNameEditError,
  isGalleryPatientAppFolder,
  GALLERY_FOLDER_KEYS,
} from "../../../helpers/galleryUtils";
import useQuery from "../../../hooks/useQuery";
import useIsMobile from "../../../hooks/useIsMobile";

const PatientImageView = ({
  data,
  refresh,
  patientData,
  activeKey,
  isLoading = false,
  setLoading,
  // When provided (the "All" tab), render one titled section per folder
  // instead of a single flat grid.
  groups,
}) => {
  const params = useQuery();
  const isAdmissionActive =
    params?.status === "current" || patientData?.admission?.is_active;
  const isFormTab = isGalleryPatientAppFolder(activeKey);
  const canEditFileName =
    activeKey !== GALLERY_FOLDER_KEYS.RECYCLE_BIN &&
    (isAdmissionActive || isFormTab);

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 768px)").matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const getFileDisplayName = (item) =>
    item?.file_name || item?.path?.split("/").pop() || "Unnamed file";

  const getModalFileName = (item) => getFileDisplayName(item) || "Gallery";

  const renderFilename = (item, className = "") => {
    const displayName = getFileDisplayName(item);

    return (
      <div className={`gallery-tile-filename-wrap ${className}`.trim()}>
        <Typography.Text
          className="gallery-tile-filename"
          style={{
            display: "block",
            fontSize: 12,
            fontWeight: 500,
            color: "#222",
            lineHeight: 1.35,
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            whiteSpace: "normal",
          }}
          title={displayName}
        >
          {displayName}
        </Typography.Text>
      </div>
    );
  };

  const TILE_W = 208;
  // Min height; cards grow with full filename (no clamp / expand)
  const TILE_H = 248;
  const TILE_RADIUS = 7.31;
  const TILE_BORDER = 0.73;
  const MEDIA_H = 140;

  const isImageFile = (item) =>
    item?.type?.includes("image") ||
    /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(item?.path || "") ||
    /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(item?.file_name || "");

  const isVideoFile = (item) => {
    if (item?.type?.includes("video")) return true;
    const haystack = `${item?.path || ""} ${item?.url || ""} ${item?.file_name || ""}`;
    return /\.(mp4|mov|avi|webm|m4v)(\?|$)/i.test(haystack);
  };

  const [selectedItem, setSelectedItem] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editName, setEditName] = useState("");
  const inputRef = useRef(null);
  const buildFileData = (items = data) =>
    items?.flatMap((item) => {
      const rawPaths =
        item?.s3_paths ??
        (item?.s3_path ? [item.s3_path] : null) ??
        item?.paths ??
        [];

      const paths = rawPaths.filter((p) => p && Object.keys(p).length > 0);
      const pathMeta = item?.paths ?? [];

      return paths.map((action, pathIndex) => {
        const meta = pathMeta[pathIndex] ?? {};
        const storedPath = meta?.path ?? action?.path ?? action?.key;

        return {
          ...action,
          ...meta,
          url: action?.url,
          path: storedPath,
          file_name: meta?.file_name ?? action?.file_name,
          type: action?.type ?? meta?.type,
          date: item?.uploaded_at,
          upload_id: item?.upload_id,
          uploaded_by_name: item?.uploaded_by_name,
          deleted_by: action?.deleted_by ?? meta?.deleted_by ?? item?.deleted_by,
          deleted_at: action?.deleted_at ?? meta?.deleted_at ?? item?.deleted_at,
        };
      });
    }) ?? [];

  // Sections share one continuous index space so inline edit (editingIndex)
  // and the lightbox (selectedItem) keep pointing at the right tile.
  const rawSections =
    groups?.length > 0
      ? groups.map((group) => ({
          key: group?.key,
          title: group?.title,
          files: sortGalleryFilesByName(buildFileData(group?.data)),
        }))
      : [
          {
            key: "__all__",
            title: null,
            files: sortGalleryFilesByName(buildFileData()),
          },
        ];

  let indexCursor = 0;
  const sections = rawSections
    .filter((section) => section.files.length > 0)
    .map((section) => ({
      ...section,
      files: section.files.map((file) => ({ file, index: indexCursor++ })),
    }));

  const fileData = sections.flatMap((section) =>
    section.files.map((entry) => entry.file)
  );
  const handleSelectItem = (index) => setSelectedItem(index);

  // Reset inline edit state when switching tabs / data changes
  useEffect(() => {
    setEditingIndex(null);
    setEditName("");
  }, [activeKey, data]);

  // --- Inline Edit ---
  const handleEditClick = (e, item, index) => {
    e.stopPropagation();
    setEditingIndex(index);
    setEditName(item?.file_name || item?.path?.split("/").pop() || "");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleEditConfirm = (item) => {
    const trimmedName = editName.trim();
    const validationError = validateGalleryFileName(trimmedName);
    if (validationError) {
      message.error(validationError);
      return;
    }

    onEditImage({ ...item, file_name: trimmedName });
    setEditingIndex(null);
    setEditName("");
  };

  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditName("");
  };

  const onEditImage = (item) => {
    setLoading(true);
    const payload = {
      paths: [
        {
          path: item?.path,
          file_name: item?.file_name,
        },
      ],
    };
    updateGalleryNameApi(item?.upload_id, payload)
      .then((res) => {
        message.success(res?.data?.message || "File name updated successfully");
        refresh?.();
        setLoading(false);
      })
      .catch((err) => {
        message.error(err?.response?.data?.message || "Something went wrong");
        setLoading(false);
      });
  };

  const handleDelete = (item) => {
    const payload = {
      ip_admission_id: patientData?.admission?.id,
      path: item?.path,
    };
    const otherPayload = {
      upload_id: item?.upload_id,
      path: item?.path,
    };

    deletePatientUploadsApi(
      isGalleryPatientAppFolder(activeKey) ? payload : otherPayload
    )
      .then((res) => {
        message.success(res?.data?.message || "Deleted successfully");
        refresh?.();
      })
      .catch((err) => {
        message.error(err?.response?.data?.message || "Delete failed");
      });
  };

  const isPdfFile = (item) =>
    item?.type?.includes("pdf") ||
    /\.pdf$/i.test(item?.path || "") ||
    /\.pdf$/i.test(item?.file_name || "");

  const renderMedia = (item) => {
    if (isVideoFile(item)) {
      return (
        <>
          <ReactPlayer
            url={item?.url}
            height={`${MEDIA_H}px`}
            width="100%"
            className="react-player"
            playsinline
            controls={!isMobile}
          />
          {isMobile && (
            <div className="gallery-media-video-overlay" aria-hidden="true">
              <PlayCircleOutlined className="gallery-media-video-play-icon" />
            </div>
          )}
        </>
      );
    }

    if (isImageFile(item)) {
      return (
        <img
          className="gallery-tile-img"
          alt={item?.path?.replace("curengo/", "") || "Image"}
          src={item?.url}
        />
      );
    }

    if (isPdfFile(item)) {
      return (
        <GridPdfViewer
          className="img-card"
          data={[item]}
          pageNumber={1}
          height={`${MEDIA_H}px`}
          width="100%"
          fill
        />
      );
    }

    return (
      <GridPdfViewer
        className="img-card"
        data={[item]}
        pageNumber={1}
        height={`${MEDIA_H}px`}
        width="100%"
        fill
      />
    );
  };

  const renderTile = (item, index) => {
    const isEditing = editingIndex === index;

    return (
      <Col
        key={item?.url || item?.path || index}
        style={{
          flex: isMobile ? "0 0 100%" : `0 0 ${TILE_W}px`,
          maxWidth: isMobile ? "100%" : TILE_W,
        }}
      >
        {/* Card container */}
        <div
          className={[
            "gallery-tile-card",
            isEditing ? "gallery-tile-editing" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={{
            width: isMobile ? "100%" : TILE_W,
            height: "auto",
            minHeight:
              activeKey === "recycle_bin"
                ? undefined
                : isEditing
                ? 268
                : TILE_H,
            borderRadius: TILE_RADIUS,
            overflow: "visible",
            border: `${TILE_BORDER}px solid #EAECF0`,
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Media area */}
          <div
            className={[
              "gallery-media-box",
              isMobile && isVideoFile(item)
                ? "gallery-media-box--video-tile"
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
            style={{
              cursor: "pointer",
              height: MEDIA_H,
              overflow: "hidden",
              flexShrink: 0,
            }}
            onClick={() => handleSelectItem(index)}
          >
            {renderMedia(item)}
          </div>
          {/* Info row below image */}
          {activeKey !== "recycle_bin" ? (
            <div
              className="gallery-tile-info"
              style={{
                padding: "8px 8px 12px",
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: isEditing ? 100 : 96,
                boxSizing: "border-box",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* File name — inline editable */}
              {canEditFileName && isEditing ? (
                <div style={{ marginBottom: 4 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <Input
                      ref={inputRef}
                      size="small"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onPressEnter={() => handleEditConfirm(item)}
                      status={
                        getGalleryFileNameEditError(editName)
                          ? "error"
                          : undefined
                      }
                      style={{ flex: 1, fontSize: 13 }}
                    />
                    <CheckOutlined
                      onClick={() => handleEditConfirm(item)}
                      style={{
                        color: "#52c41a",
                        cursor: "pointer",
                        fontSize: 14,
                      }}
                    />
                    <CloseOutlined
                      onClick={handleEditCancel}
                      style={{
                        color: "#ff4d4f",
                        cursor: "pointer",
                        fontSize: 14,
                      }}
                    />
                  </div>
                  {getGalleryFileNameEditError(editName) && (
                    <Typography.Text
                      type="danger"
                      style={{
                        display: "block",
                        fontSize: 11,
                        marginTop: 4,
                        lineHeight: "14px",
                      }}
                    >
                      {getGalleryFileNameEditError(editName)}
                    </Typography.Text>
                  )}
                </div>
              ) : (
                renderFilename(item)
              )}

              {/* Date */}
              <Typography.Text
                style={{ fontSize: 11, color: "#888" }}
              >
                {item?.date
                  ? dayjs(item.date).format("DD MMM, hh:mm A")
                  : "Unknown Date"}
              </Typography.Text>

              {/* Bottom row: Uploaded by + actions */}
              {editingIndex !== index && (
                <div style={{ flex: 1 }} />
              )}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  marginTop: isEditing ? 8 : 6,
                  flexShrink: 0,
                }}
              >
                <Tooltip title={item?.uploaded_by_name || "-"}>
                  <Typography.Text
                    ellipsis
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 11,
                      color: "#667085",
                    }}
                  >
                    Uploaded By: {item?.uploaded_by_name || "-"}
                  </Typography.Text>
                </Tooltip>

                {canEditFileName && editingIndex !== index && (
                  <div style={{ display: "flex", gap: 10 }}>
                    <EditIcon
                      onClick={(e) =>
                        handleEditClick(e, item, index)
                      }
                      style={{
                        color: "#888",
                        cursor: "pointer",
                        fontSize: 13,
                      }}
                    />
                    {isAdmissionActive && (
                      <Popconfirm
                        title="Are you sure you want to delete this file?"
                        okText="Yes"
                        cancelText="No"
                        onConfirm={() => handleDelete(item)}
                      >
                        <DeleteOutlined
                          style={{
                            color: "#ff4d4f",
                            cursor: "pointer",
                            fontSize: 13,
                          }}
                        />
                      </Popconfirm>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div
              style={{ padding: "8px 6px 10px" }}
              onClick={(e) => e.stopPropagation()}
            >
              {renderFilename(
                item,
                "gallery-tile-filename-wrap--recycle"
              )}

              <Typography.Text
                style={{
                  display: "block",
                  fontSize: 11,
                  color: "#667085",
                  lineHeight: "15px",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                <span style={{ fontWeight: 600, color: "#344054" }}>
                  Deleted By:
                </span>{" "}
                {item?.deleted_by || "-"}
              </Typography.Text>

              <Typography.Text
                style={{
                  display: "block",
                  fontSize: 11,
                  color: "#667085",
                  marginTop: 2,
                  lineHeight: "15px",
                }}
              >
                <span style={{ fontWeight: 600, color: "#344054" }}>
                  Deleted At:
                </span>{" "}
                {item?.deleted_at
                  ? dayjs(item.deleted_at).format("DD MMM, hh:mm A")
                  : "Unknown Date"}
              </Typography.Text>
            </div>
          )}
        </div>
      </Col>
    );
  };

  return (
    <>
      <div
        style={{
          width: "100%",
          maxWidth: "100%",
          overflowX: "hidden",
          overflowY: "visible",
          height: "auto",
          minHeight: isLoading ? 240 : 0,
          paddingBottom: "calc(32px + env(safe-area-inset-bottom, 0px))",
          backgroundColor: "#fff",
          position: "relative",
        }}
        className="gallery-card-container"
      >
        {isLoading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#fff",
              zIndex: 10,
            }}
          >
            <Spin tip="Loading patient gallery data, please wait ..." />
          </div>
        )}
        {fileData?.length > 0 ? (
          <div
            className="gallery-img-container"
            style={{
              overflow: "visible",
              display: "flex",
              flexDirection: "column",
              width: "100%",
              backgroundColor: "#fff",
            }}
          >
            {sections.map((section) => (
              <div className="gallery-folder-section" key={section.key}>
                {section.title && (
                  <div className="gallery-folder-heading">
                    <Typography.Text className="gallery-folder-heading-text">
                      {section.title}
                    </Typography.Text>
                    <Typography.Text className="gallery-folder-heading-count">
                      {section.files.length}
                    </Typography.Text>
                  </div>
                )}
                <Row
                  style={{
                    width: "100%",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 16,
                  }}
                >
                  {section.files.map(({ file, index }) =>
                    renderTile(file, index)
                  )}
                </Row>
              </div>
            ))}
          </div>
        ) : !isLoading ? (
          <Row
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "100%",
              backgroundColor: "white",
              width: "100vw",
              maxWidth: "100%",
              overflowX: "hidden",
            }}
          >
            <Col span={24} style={{ textAlign: "center", padding: "20px" }}>
              <NoDataPage
                title={"Gallery Empty"}
                subtitle={"No images to be shown at the moment!"}
                image={GalleryIcon}
              />
            </Col>
          </Row>
        ) : null}
      </div>

      {/* Gallery viewer modal */}
      {typeof selectedItem === "number" && (
        <Modal
          open={typeof selectedItem === "number"}
          onCancel={() => setSelectedItem(null)}
          width="min(920px, 96vw)"
          footer={null}
          centered
          destroyOnClose
          maskClosable={false}
          wrapClassName="patient-gallery-lightbox"
          title={
            <Row>
              <Col span={24}>
                <Typography.Text strong ellipsis className="gallery-lightbox-modal-title">
                  {getModalFileName(fileData[selectedItem])}
                </Typography.Text>
              </Col>
            </Row>
          }
        >
          <PatientGalleryScroll
            selectedGroup={fileData}
            selectedItem={selectedItem}
          />
        </Modal>
      )}
    </>
  );
};

export default PatientImageView;
